import { DEFAULT_CURRENCY_SYMBOL } from '@/lib/currency';
import {
  coerceBreedAverageRaw,
  formatBreedAverageDisplay,
} from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import {
  createEbvValueFormatter,
  getEbvDisplayCategory,
  getEbvDisplayTint,
  type EbvAverageData,
  type EbvConfigItem,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import type { AnimalEbvRecord } from '../types';

/** Per-cell styling context for an EBV column, shared by the header, breed-average and value rows. */
export interface EbvCellContext {
  /** True for index columns (`display === 2`), rendered with a purple outline. */
  highlighted: boolean;
  /** Whether the previous column was also an index column (dedupes the shared outline border). */
  prevHighlighted: boolean;
  /** Category tint colour for this column. */
  tint: string;
  /** Whether the previous column is a non-index column in the same category (dedupes the block border). */
  previousSameCategory: boolean;
}

/**
 * Derive the highlight/category-border context for the EBV column at `index`.
 * Centralises the "is this an index column / does it continue the previous
 * column's category block" logic shared across the table header, breed-average
 * row, and the per-animal value rows.
 */
export function getEbvCellContext(
  ebvConfig: EbvConfigItem,
  previousEbvConfig: EbvConfigItem | undefined,
  groupingOptions?: EbvGroupingOptions,
): EbvCellContext {
  const category = getEbvDisplayCategory(ebvConfig, groupingOptions);
  return {
    highlighted: ebvConfig.display === 2,
    prevHighlighted: previousEbvConfig?.display === 2,
    tint: getEbvDisplayTint(ebvConfig, groupingOptions),
    previousSameCategory:
      previousEbvConfig != null &&
      previousEbvConfig.display !== 2 &&
      getEbvDisplayCategory(previousEbvConfig, groupingOptions) === category,
  };
}

/**
 * Case-insensitive property lookup.
 * ASP.NET Core CamelCase turns `EBV1` -> `eBV1`, but GraphQL uses `ebv1`.
 * This handles both by building a lowercase key map on first access.
 */
const lowerKeyCache = new WeakMap<AnimalEbvRecord, Record<string, unknown>>();

function getLowerKeyMap(animal: AnimalEbvRecord): Record<string, unknown> {
  let cached = lowerKeyCache.get(animal);
  if (!cached) {
    cached = {};
    for (const [key, value] of Object.entries(animal)) {
      cached[key.toLowerCase()] = value;
    }
    lowerKeyCache.set(animal, cached);
  }
  return cached;
}

function toNumber(rawValue: unknown): number | null {
  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : null;
  }
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function getEbvValue(
  animal: AnimalEbvRecord,
  ebvNumber: number,
): number | null {
  const lowerKeyMap = getLowerKeyMap(animal);
  return toNumber(lowerKeyMap[`ebv${ebvNumber}`]);
}

export function formatEbv(
  value: number | null | undefined,
  format: string,
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
): string {
  if (value == null) return '';
  return createEbvValueFormatter(format, currencySymbol)(String(value));
}

export function getBreedAverageRaw(
  ebvAvg: EbvAverageData,
  ebvConfig: EbvConfigItem,
): string | number | null | undefined {
  const fieldNumber = ebvConfig.field.replace(/[^\d]/g, '');
  return coerceBreedAverageRaw(ebvAvg[`ebv_${fieldNumber}_avg`]);
}

/** Plain-text breed average (first ILR2 line when dual-line HTML). */
export function getBreedAverageValue(
  ebvAvg: EbvAverageData,
  ebvConfig: EbvConfigItem,
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
): string {
  const raw = getBreedAverageRaw(ebvAvg, ebvConfig);
  if (raw === undefined || raw === null || raw === '') return '';
  return formatBreedAverageDisplay(
    raw,
    ebvConfig.format || '1',
    currencySymbol,
  );
}

export function csvEscape(value: string): string {
  const formulaLike =
    /^[\s]*[=+@]/.test(value) ||
    (/^[\s]*-/.test(value) && !/^[\s]*-\d+(?:\.\d+)?%?\s*$/.test(value));
  const safeValue = formulaLike ? `'${value}` : value;
  if (/[",\r\n]/.test(safeValue)) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
  return safeValue;
}
